/**
 * Magic sign-in exchange page. The firm-issued link format is
 *   /magic?t=<jwt>
 * We POST the token to /api/auth/magic-login which validates expiry +
 * purpose claim, mints a fresh session, and the page redirects to the
 * dashboard (or /client for client-role users).
 *
 * While the exchange is in flight (usually <500ms) we paint the loading
 * screen in the firm's branding — logo, name, and theme colors pulled
 * from the public /api/public/tenant-theme endpoint keyed off the
 * tenant_id claim inside the JWT. The claim is read client-side without
 * signature verification (the server still enforces validity on
 * exchange) so the loader stays on-brand even if the token has already
 * expired.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

// Base64-url decode the JWT payload WITHOUT verifying the signature. We
// only use it to extract the public tenant_id claim so the loading
// screen can paint in the firm's palette. The real validation happens
// server-side when we POST /auth/magic-login.
function peekJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch { return null; }
}

export default function MagicLogin() {
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const token = searchParams.get("t");
  const [status, setStatus] = useState("exchanging"); // exchanging | failed
  const [error, setError] = useState("");
  const [branding, setBranding] = useState(null); // {name, row_hover, accent, brand_color, logo_base64}
  const didExchange = useRef(false);

  // Peek the JWT once per mount to pull out the embedded tenant_id.
  const tenantId = useMemo(() => peekJwtPayload(token)?.tenant_id, [token]);

  // Fetch public branding + apply CSS variables so the loader (and the
  // "link expired" fallback) is themed before the real session mounts.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/tenant-theme?tenant=${encodeURIComponent(tenantId)}`);
        if (cancelled || !data?.tenant_id) return;
        setBranding(data);
        const root = document.documentElement;
        if (data.row_hover) root.style.setProperty("--ll-row-hover", data.row_hover);
        if (data.accent) {
          root.style.setProperty("--ll-accent-soft", data.accent);
          root.style.setProperty("--ll-mint", data.accent);
        }
      } catch { /* silent — default theme is fine */ }
    })();
    return () => {
      cancelled = true;
      // Reset CSS vars when leaving the page so subsequent routes aren't
      // stuck on this firm's palette.
      const root = document.documentElement;
      root.style.removeProperty("--ll-row-hover");
      root.style.removeProperty("--ll-accent-soft");
      root.style.removeProperty("--ll-mint");
    };
  }, [tenantId]);

  useEffect(() => {
    if (!token || didExchange.current) return;
    didExchange.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-login", { token });
        setSession(data.token, data.user, data.tenant);
        toast.success(`Signed in to ${data.tenant?.name || "workspace"}`);
        navigate(data.user?.role === "client" ? "/client" : "/app/dashboard", { replace: true });
      } catch (err) {
        setStatus("failed");
        setError(err?.response?.data?.detail || "Could not use this link.");
      }
    })();
  }, [token, setSession, navigate]);

  if (!token) return <Navigate to="/login" replace />;

  const hero = branding?.brand_color || "var(--ll-ink)";

  return (
    <div
      className="min-h-screen grid place-items-center p-8 relative"
      style={{ background: branding?.accent || "var(--ll-bg)" }}
      data-testid="magic-login-page"
    >
      <div
        className="ll-card-flat p-8 max-w-md w-full text-center shadow-sm"
        data-testid="magic-login-card"
        style={{ borderTop: `3px solid ${hero}` }}
      >
        {branding?.logo_base64 ? (
          <img
            src={branding.logo_base64}
            alt={branding.name || "logo"}
            className="mx-auto mb-4 h-12 w-auto"
            style={{ maxWidth: 200 }}
            data-testid="magic-login-logo"
          />
        ) : branding?.name ? (
          <div
            className="mx-auto mb-4 w-12 h-12 grid place-items-center text-white font-display font-bold"
            style={{ background: hero }}
          >
            {branding.name.trim().charAt(0).toUpperCase()}
          </div>
        ) : null}

        {status === "exchanging" && (
          <>
            <Loader2 className="mx-auto animate-spin mb-3" size={28} style={{ color: hero }} />
            <div className="font-display font-bold text-lg" data-testid="magic-login-title">
              {branding?.name ? `Signing you in to ${branding.name}…` : "Signing you in…"}
            </div>
            <p className="text-sm text-[var(--ll-ink-soft)] mt-1">
              Validating your magic link.
            </p>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertTriangle className="mx-auto mb-3 text-[var(--ll-error)]" size={28} />
            <div className="font-display font-bold text-lg">Link no longer works</div>
            <p className="text-sm text-[var(--ll-ink-soft)] mt-1 mb-4" data-testid="magic-login-error">
              {error}
            </p>
            <Link
              to={branding?.slug ? `/login?f=${branding.slug}` : (tenantId ? `/login?tenant=${tenantId}` : "/login")}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-white hover:opacity-90 text-sm"
              style={{ background: hero }}
              data-testid="magic-login-fallback"
            >
              Sign in with password instead
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
